<template>
  <v-container fluid class="pa-0">
    <div class="d-flex justify-space-between align-center flex-wrap">
      <div>
        <h2 class="subheading grey--text">Reuniones</h2>
        <p class="grey--text text--darken-1 mb-0">{{ proximasCount }} próximas · {{ finalizadasCount }} finalizadas · {{ canceladasCount }} canceladas</p>
      </div>
      <div>
        <v-btn color="primary" @click="openNew()">
          <v-icon left>mdi-plus</v-icon>
          Nueva reunión
        </v-btn>
      </div>
    </div>

    <v-tabs v-model="tab" color="primary" class="mt-3">
      <v-tab>Próximas</v-tab>
      <v-tab>Finalizadas</v-tab>
      <v-tab>Canceladas</v-tab>
    </v-tabs>
    <v-divider></v-divider>

    <v-card class="elevation-2 mt-4">
      <v-data-table
        :headers="headers"
        :items="filteredReuniones"
        :footer-props="{
          itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
          itemsPerPageText: 'Filas por página'
        }"
        :no-data-text="'No hay reuniones en este estado'"
        class="elevation-0"
      >
        <template v-slot:item.titulo="{ item }">
          <div class="font-weight-medium">{{ item.titulo }}</div>
          <div class="caption grey--text text-wrap">{{ item.descripcion }}</div>
        </template>
        <template v-slot:item.fecha="{ item }">
          {{ item.fecha }}<br><span class="caption grey--text">{{ item.hora }}</span>
        </template>
        <template v-slot:item.lugar="{ item }">
          <v-icon small class="mr-1">{{ item.modalidad === 'Virtual' ? 'mdi-video' : 'mdi-map-marker' }}</v-icon>{{ item.lugar }}
        </template>
        <template v-slot:item.asistencia="{ item }">
          {{ item.asistentesConfirmados }} / {{ item.totalParticipantes }}
        </template>
        <template v-slot:item.estado="{ item }">
          <v-chip small :color="estadoColor(item.estado)" text-color="white">{{ item.estado }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on" :to="`/mesa_directiva/reuniones/${item.id}`" @click="$router.push(`/mesa_directiva/reuniones/${item.id}`)">mdi-eye-outline</v-icon>
            </template>
            <span>Ver reunión</span>
          </v-tooltip>
          <v-tooltip bottom v-if="item.estado === 'Próxima'">
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on" @click="cancelar(item)">mdi-close-circle-outline</v-icon>
            </template>
            <span>Cancelar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent scrollable max-width="560">
      <v-form @submit.prevent="save()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>Nueva reunión</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-5">
            <v-text-field v-model="form.titulo" label="* Tema principal" prepend-icon="mdi-format-title" outlined dense></v-text-field>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="form.fecha"
                  label="* Fecha"
                  type="date"
                  prepend-icon="mdi-calendar"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="form.hora"
                  label="* Hora"
                  type="time"
                  prepend-icon="mdi-clock-outline"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-select v-model="form.modalidad" :items="['Presencial', 'Virtual']" label="* Modalidad" prepend-icon="mdi-map-marker-outline" outlined dense></v-select>
            <v-text-field v-model="form.lugar" label="* Lugar / enlace" prepend-icon="mdi-map-marker" outlined dense></v-text-field>
            <v-textarea v-model="form.descripcion" label="Descripción" prepend-icon="mdi-text-long" outlined dense rows="3"></v-textarea>
            <v-autocomplete
              v-model="form.participantes"
              :items="miembros"
              item-text="name"
              item-value="id"
              label="Participantes"
              prepend-icon="mdi-account-multiple-outline"
              multiple
              small-chips
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-card-text>
          <v-card-actions>
            <v-btn color="grey darken-1" text @click="dialog = false">Cerrar</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :loading="loading" :disabled="loading">Crear reunión</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<script>
import mesaDirectivaService from '@/services/mesaDirectivaService'
import { estadoColor } from './mesaDirectivaMockData'

export default {
  name: 'MesaDirectivaReuniones',
  data () {
    return {
      reuniones: [],
      miembros: [],
      tab: 0,
      dialog: false,
      loading: false,
      form: this.emptyForm(),
      statusMap: ['upcoming', 'finalized', 'cancelled'],
      statusDisplay: ['Próxima', 'Finalizada', 'Cancelada'],
      headers: [
        { text: 'Reunión', value: 'titulo' },
        { text: 'Fecha / hora', value: 'fecha' },
        { text: 'Lugar', value: 'lugar' },
        { text: 'Asistencia', value: 'asistencia' },
        { text: 'Estado', value: 'estado' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  async mounted () {
    await this.loadData()
  },
  computed: {
    miembrosNombres () {
      return this.miembros.map(m => ({ text: m.name, value: m.id }))
    },
    proximasCount () {
      return this.reuniones.filter(r => r.estado === 'Próxima').length
    },
    finalizadasCount () {
      return this.reuniones.filter(r => r.estado === 'Finalizada').length
    },
    canceladasCount () {
      return this.reuniones.filter(r => r.estado === 'Cancelada').length
    },
    filteredReuniones () {
      return this.reuniones.filter(r => r.estado === this.statusDisplay[this.tab])
    }
  },
  methods: {
    estadoColor,
    emptyForm () {
      return { titulo: '', fecha: '', hora: '', modalidad: 'presencial', lugar: '', descripcion: '', participantes: [], agenda: [] }
    },
    async loadData () {
      this.loading = true
      try {
        // Cargar miembros para el select
        const members = await mesaDirectivaService.getMembers()
        this.miembros = members

        // Cargar todas las reuniones (próximas, finalizadas, canceladas)
        const allMeetings = await mesaDirectivaService.getMeetings()
        this.mapearReuniones(allMeetings)
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message || 'Error al cargar reuniones')
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    mapearReuniones (reuniones) {
      this.reuniones = reuniones.map(r => ({
        ...r,
        titulo: r.title,
        fecha: this.formatearFecha(r.date),
        hora: r.time,
        lugar: r.location,
        modalidad: r.modality,
        descripcion: r.description,
        estado: this.mapearEstado(r.status),
        asistentesConfirmados: r.confirmed_attendees,
        totalParticipantes: r.total_participants,
        agenda: r.agenda || [],
        participantes: r.participants || [],
        acuerdosGeneradosIds: r.agreements_generated_ids || []
      }))
    },
    mapearEstado (status) {
      const map = { upcoming: 'Próxima', finalized: 'Finalizada', cancelled: 'Cancelada' }
      return map[status] || status
    },
    formatearFecha (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    openNew () {
      this.form = this.emptyForm()
      this.dialog = true
    },
    async cancelar (item) {
      try {
        await mesaDirectivaService.cancelMeeting(item.id)
        item.estado = 'Cancelada'
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Reunión cancelada correctamente')
        this.$store.commit('snackbar_activate_true')
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      }
    },
    async save () {
      if (!this.form.titulo || !this.form.fecha || !this.form.hora || !this.form.lugar) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Completa los campos obligatorios de la reunión')
        this.$store.commit('snackbar_activate_true')
        return
      }

      try {
        this.loading = true
        const payload = {
          title: this.form.titulo,
          date: this.formatearFechaParaAPI(this.form.fecha),
          time: this.form.hora,
          location: this.form.lugar,
          modality: this.form.modalidad,
          description: this.form.descripcion || '',
          participant_ids: this.form.participantes,
          agenda: this.form.agenda || []
        }

        const response = await mesaDirectivaService.createMeeting(payload)
        this.reuniones.unshift(this.mapearUnionReunion(response))

        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Reunión creada correctamente')
        this.$store.commit('snackbar_activate_true')
        this.dialog = false
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    mapearUnionReunion (reunionAPI) {
      return {
        ...reunionAPI,
        titulo: reunionAPI.title,
        fecha: this.formatearFecha(reunionAPI.date),
        hora: reunionAPI.time,
        lugar: reunionAPI.location,
        modalidad: reunionAPI.modality,
        descripcion: reunionAPI.description,
        estado: this.mapearEstado(reunionAPI.status),
        asistentesConfirmados: reunionAPI.confirmed_attendees,
        totalParticipantes: reunionAPI.total_participants,
        participantes: reunionAPI.participants || [],
        acuerdosGeneradosIds: reunionAPI.agreements_generated_ids || []
      }
    },
    formatearFechaParaAPI (fechaStr) {
      if (!fechaStr) return ''
      // El input type="date" retorna directamente en formato YYYY-MM-DD
      return fechaStr
    }
  }
}
</script>
